import React from 'react';
import { resolveSubjectAggregate } from './aggregateUtils';

const gradeColor = (grade) => {
  if (!grade) return '#2e7d32';
  const g = grade.toUpperCase();
  if (!/^[ABCD]/.test(g)) return '#1565c0';
  if (g.startsWith('A')) return '#1b5e20';
  if (g.startsWith('B')) return '#2e7d32';
  if (g.startsWith('C')) return '#e65100';
  return '#b71c1c';
};

const assessmentCellValue = (assessment) => {
  if (!assessment) return '-';
  if (assessment.is_absent) return 'ABS';
  if (assessment.is_exempted) return 'EXM';
  if (assessment.rubric_rating_label) return assessment.rubric_rating_label;
  const weighted = assessment.weighted_score;
  if (weighted != null) return Number(weighted).toFixed(0);
  const raw = assessment.score ?? assessment.marks_obtained ?? assessment.raw_score;
  if (raw == null) return '-';
  return Number(raw).toFixed(0);
};

const isProjectAssessment = (code) => {
  if (!code) return false;
  const projectKeywords = /^(proj|project|assign|assignment|prac|practical|coursework|cw)/i;
  return projectKeywords.test(code);
};

export default function SecondarySubjectsTable({ report, showSubjectComments = true, showSubjectRank = false, showAggregate = true, showMarks = true, showGrade = true, highlightProjects = false }) {
  const subjects = React.useMemo(() => report?.subjects_data || [], [report?.subjects_data]);

  const assessmentCols = React.useMemo(() => {
    const map = new Map();
    const projectKeywords = /^(proj|project|assign|assignment|prac|practical|coursework|cw)/i;
    
    subjects.forEach((sub) => {
      (sub.papers || []).forEach((paper) => {
        (paper.assessments || []).forEach((a) => {
          if (a.include_in_final === false || a.policy_included === false) return;
          const code = a.assessment_type?.assessment_code || a.assessment_code;
          // Skip project assessments - they go in ProjectsTable
          if (code && !projectKeywords.test(code) && !map.has(code)) {
            map.set(code, { code, max_marks: a.max_marks, weight: a.weight_percentage });
          }
        });
      });
    });
    const caKeywords = /^(bot|mot|ca|mid|cont|test|quiz|hw|home)/i;
    const eotKeywords = /^(eot|exam|end|final|term)/i;
    const order = (code) => {
      if (caKeywords.test(code)) return 0;
      if (eotKeywords.test(code)) return 2;
      return 1;
    };
    return Array.from(map.values()).sort((a, b) => order(a.code) - order(b.code));
  }, [subjects]);

  const singleCol = assessmentCols.length === 1;
  const showAggCol = showAggregate && showGrade;

  return (
    <>
      <p className="rc-section-title">Academic Progress</p>

      <table className="rc-table">
        <thead>
          <tr>
            <th className="left" style={{ width: '15%' }}>Subject</th>
            {showMarks && assessmentCols.map((col) => (
              <th key={col.code} style={{ textAlign: 'center', width: '5%' }}>
                {col.code}<br />
                <span style={{ fontWeight: 'normal', fontSize: '7pt' }}>({col.weight}%)</span>
              </th>
            ))}
            {showMarks && !singleCol && <th style={{ width: '5%', textAlign: 'center' }}>Total<br /><span style={{ fontWeight: 'normal', fontSize: '7pt' }}>(/100)</span></th>}
            {showSubjectRank && <th style={{ width: '4%', textAlign: 'center' }}>Rank</th>}
            {showAggCol && <th style={{ width: '3%' }}>Agg</th>}
            {showSubjectComments && <th className="left" style={{ width: '35%' }}>Remarks</th>}
            <th style={{ width: '4%', textAlign: 'center' }}>Initials</th>
          </tr>
        </thead>
        <tbody>
          {subjects.length === 0 ? (
            <tr><td colSpan={10} style={{ textAlign: 'center', color: '#888', padding: '10px' }}>No subject data available</td></tr>
          ) : subjects.map((sub, si) => {
            const papers = sub.papers || [];
            const grade = sub.rubric_rating_label || sub.subject_grade || sub.final_grade || '-';
            const subjectAggregate = resolveSubjectAggregate(sub);
            const subjectAggregateLabel = grade && grade !== '-'
              ? grade
              : (subjectAggregate != null ? String(subjectAggregate) : '-');
            const subjectName = sub.subject?.subject_name || sub.subject?.name || `Subject ${si + 1}`;
            const subjectRemark = sub.subject_comment || sub.teacher_comment || sub.subject_remarks || '-';

            const markByCode = {};
            papers.forEach((paper) => {
              (paper.assessments || []).forEach((a) => {
                const code = a.assessment_type?.assessment_code || a.assessment_code;
                if (code) markByCode[code] = a;
              });
            });

            const totalPct = assessmentCols.reduce((sum, col) => {
              const a = markByCode[col.code];
              if (!a || a.is_absent || a.is_exempted) return sum;
              const ws = parseFloat(a.weighted_score);
              return sum + (isNaN(ws) ? 0 : ws);
            }, 0);

            return (
              <tr key={sub.subject?.id || si}>
                <td className="subject-name">{subjectName}</td>
                {showMarks && assessmentCols.map((col) => {
                  const a = markByCode[col.code];
                  const val = assessmentCellValue(a);
                  const isExcluded = a?.policy_included === false;
                  return (
                    <td
                      key={col.code}
                      style={{
                        textAlign: 'center',
                        fontSize: '8.5pt',
                        color: isExcluded ? '#9e9e9e' : 'inherit',
                        fontStyle: isExcluded ? 'italic' : 'normal'
                      }}
                    >
                      {val}{isExcluded ? '*' : ''}
                    </td>
                  );
                })}
                {showMarks && !singleCol && (
                  <td style={{ textAlign: 'center', fontWeight: 'bold', fontSize: '8.5pt' }}>
                    {totalPct.toFixed(0)}
                  </td>
                )}
                {showSubjectRank && (
                  <td style={{ textAlign: 'center', fontWeight: 'bold', color: '#555' }}>
                    {sub.subject_rank != null && sub.subject_total_students != null
                      ? `${sub.subject_rank}/${sub.subject_total_students}`
                      : '-'}
                  </td>
                )}
                {showAggCol && (
                  <td style={{ textAlign: 'center' }}>
                    <span className="rc-grade-badge" style={{ color: gradeColor(grade) }}>
                      {subjectAggregateLabel}
                    </span>
                  </td>
                )}
                {showSubjectComments && (
                  <td style={{ fontSize: '8.5pt', fontStyle: 'italic', color: '#444' }}>{subjectRemark}</td>
                )}
                <td style={{ textAlign: 'center', fontSize: '8.5pt', fontWeight: 'bold' }}>
                  {(() => {
                    let initials = null;
                    (sub.papers || []).forEach((p) => { if (!initials && p.teacher_initials) initials = p.teacher_initials; });
                    return initials || '';
                  })()}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
}
